import type { ReactNode } from 'react'
import { useT } from '../i18n'
import { Landing } from '../screens/Landing'
import { Skeleton } from '../ui'
import { useSession } from './session'

/**
 * Gates a subtree on there being a session, and is the ONLY thing that may
 * render a component calling `useAuthenticatedSession`.
 *
 * This is a convenience, NOT a security boundary. The BFF rejects
 * unauthenticated calls and the Go API re-derives the biller scope from the
 * JWT on every request; a client-side guard can always be bypassed and must
 * never be the only thing between a user and someone else's data.
 *
 * Anonymous renders the landing page in place rather than redirecting: an
 * expired session should not throw away the URL the user was on, and an
 * explanation beats a silent bounce to Keycloak.
 */
export function RequireSession({ children }: { readonly children: ReactNode }) {
  const { status } = useSession()
  const t = useT()

  if (status === 'loading') return <Skeleton count={4} variant="row" label={t('common.loading')} />
  if (status === 'anonymous') return <Landing />
  return <>{children}</>
}
